// Core Analytics Types and Interfaces

export type InstrumentType = string;
export type InstrumentCategory = string;

// Configuration Interfaces
export interface AnalyticsConfig {
  trackingId: string;
  enabled: boolean;
  respectDNT: boolean;
  debugMode: boolean;
}

export interface GA4Config {
  anonymizeIp?: boolean;
  cookieFlags?: string;
  customDimensions?: Record<string, string>;
  customMetrics?: Record<string, number>;
}

export interface GA4EventParams {
  [key: string]: string | number | boolean;
}

// Event Processing Interfaces
export interface ProcessOptions {
  debounceMs?: number;
  throttleMs?: number;
  batch?: boolean;
  priority?: 'high' | 'normal' | 'low';
}

export interface AnalyticsEvent {
  name: string;
  data: EventData;
  timestamp: number;
  sessionId: string;
}

export type EventData = Record<string, any>;

// Session Event Data
export interface SessionStartData {
  userType: 'authenticated' | 'anonymous';
  userId?: string;
  referrerSource: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  screenResolution: string;
  timestamp: number;
}

export interface SessionEndData {
  sessionId: string;
  sessionDuration: number;
  totalEvents: number;
  primaryActivities: string[];
  featureTimeSpent: Record<string, number>;
  engagementScore: number;
  timestamp: number;
}

// Instrument Event Data
export interface InstrumentSelectionData {
  instrumentType: InstrumentType;
  instrumentCategory: InstrumentCategory;
  rowIndex: number;
  timestamp: number;
  sessionId: string;
}

export interface InstrumentChangeData {
  previousInstrumentType: InstrumentType;
  newInstrumentType: InstrumentType;
  rowIndex: number;
  timestamp: number;
  sessionId: string;
}

export interface PresetApplicationData {
  instrumentType: InstrumentType;
  presetName: string;
  presetId: string;
  isUserCreated: boolean;
  isDefault: boolean;
  rowIndex: number;
  timestamp: number;
  sessionId: string;
}

// Parameter Event Data
export interface ParameterAdjustmentData {
  parameterName: string;
  controlType: 'slider' | 'knob' | 'toggle' | 'select';
  previousValue: number | string | boolean;
  newValue: number | string | boolean;
  instrumentType: InstrumentType;
  rowIndex: number;
  timestamp: number;
  sessionId: string;
}

export interface ParameterChangeCompleteData {
  parameterName: string;
  finalValue: number | string | boolean;
  adjustmentDuration: number;
  instrumentType: InstrumentType;
  rowIndex: number;
  timestamp: number;
  sessionId: string;
}

export interface CustomParametersToggleData {
  instrumentType: InstrumentType;
  enabled: boolean;
  rowIndex: number;
  timestamp: number;
  sessionId: string;
}

// Grid Event Data
export interface GridCellToggleData {
  rowIndex: number;
  stepIndex: number;
  activeState: boolean;
  instrumentType: InstrumentType;
  isDragOperation: boolean;
  timestamp: number;
  sessionId: string;
}

export interface NotePitchChangeData {
  rowIndex: number;
  stepIndex: number;
  previousNote: string;
  newNote: string;
  instrumentType: InstrumentType;
  timestamp: number;
  sessionId: string;
}

export interface GridDragOperationData {
  startStep: number;
  endStep: number;
  rowIndex: number;
  cellsAffected: number;
  dragDuration: number;
  instrumentType: InstrumentType;
  timestamp: number;
  sessionId: string;
}

export interface RowActionData {
  action: 'add' | 'remove';
  rowIndex: number;
  totalRowCount: number;
  timestamp: number;
  sessionId: string;
}

// Playback Event Data
export interface PlaybackStartData {
  compositionLength: number;
  activeRowCount: number;
  activeCellCount: number;
  bpm: number;
  timestamp: number;
  sessionId: string;
}

export interface PlaybackStopData {
  playbackDuration: number;
  completedFullLoop: boolean;
  stopPosition: number;
  timestamp: number;
  sessionId: string;
}

export interface BPMChangeData {
  previousBPM: number;
  newBPM: number;
  adjustmentMethod: 'slider' | 'input';
  timestamp: number;
  sessionId: string;
}

// Composition Event Data
export interface CompositionExportData {
  exportFormat: string;
  compositionDuration: number;
  instrumentCount: number;
  activeCellCount: number;
  creditConsumed?: boolean;
  timestamp: number;
  sessionId: string;
}

export interface CompositionSaveLoadData {
  action: 'save' | 'load';
  storageSource: 'local' | 'cloud' | 'shared';
  compositionMetadata: {
    title?: string;
    duration?: number;
    instrumentCount?: number;
  };
  timestamp: number;
  sessionId: string;
}

// AI Generation Event Data
export interface AIGenerationStartData {
  promptLength: number;
  generationMode: 'new' | 'edit';
  userCreditBalance: number;
  timestamp: number;
  sessionId: string;
}

export interface AIGenerationCompleteData {
  generationDuration: number;
  cellsGenerated: number;
  instrumentsUsed: number;
  userKeptResult: boolean;
  timestamp: number;
  sessionId: string;
}

export interface AIGenerationErrorData {
  errorType: string;
  errorMessage: string;
  generationParameters: {
    promptLength: number;
    generationMode: string;
  };
  timestamp: number;
  sessionId: string;
}

// Mixing Event Data
export interface VolumeAdjustmentData {
  rowIndex: number;
  instrumentType: InstrumentType;
  previousVolume: number;
  newVolume: number;
  adjustmentMethod: 'slider' | 'input';
  timestamp: number;
  sessionId: string;
}

export interface MuteToggleData {
  rowIndex: number;
  instrumentType: InstrumentType;
  muteState: boolean;
  isPlaybackActive: boolean;
  timestamp: number;
  sessionId: string;
}

// Template Event Data
export interface TemplateLoadData {
  templateId: string;
  templateCategory: string;
  isUserCreated: boolean;
  isSystemProvided: boolean;
  timestamp: number;
  sessionId: string;
}

export interface TemplateSaveData {
  templateName: string;
  instrumentCount: number;
  activeCellCount: number;
  createdFromScratch: boolean;
  modifiedFromExisting: boolean;
  timestamp: number;
  sessionId: string;
}

// Health Monitoring
export interface AnalyticsHealthMetrics {
  eventsQueued: number;
  eventsSent: number;
  eventsFailed: number;
  lastSuccessfulSend: number;
  initializationStatus: 'pending' | 'success' | 'failed';
  optOutStatus: boolean;
}

// Error Types
export class GA4InitializationError extends Error {
  constructor(
    message: string,
    public code: 'MISSING_ID' | 'NETWORK_ERROR' | 'SCRIPT_LOAD_FAILED' | 'INVALID_CONFIG',
    public retryable: boolean
  ) {
    super(message);
    this.name = 'GA4InitializationError';
  }
}
